import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FilePdf, DownloadSimple, Share } from "@phosphor-icons/react";

/**
 * Quick PDF report button — embed inside any section page to let the user
 * download an instant PDF of what they are currently viewing.
 *
 * Props:
 *  section: required, one of clubs|tournaments|retas|dyd_run|dyd_coin|whatsapp|players|finance
 *  params:  optional, context-specific filters (club_id, tournament_id, reta_id, user_id, start_date, end_date)
 *  label:   optional, button text override
 *  variant: optional, "default" | "compact" (icon only)
 *  withShare: if true, shows a secondary "Compartir" button
 */
export default function QuickReportButton({
  section,
  params = {},
  label,
  variant = "default",
  withShare = false,
  className = "",
}) {
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(false);

  const generate = async (share = false) => {
    const setBusyFn = share ? setSharing : setBusy;
    setBusyFn(true);
    try {
      const body = { section, params: params || {}, share };
      if (share) {
        const r = await api.post("/reports/generate", body);
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/shared/${r.data.share_id}`;
        try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
        toast.success(`Link copiado · válido ${r.data.expires_in_days} días`);
      } else {
        const r = await api.post("/reports/generate", body, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `${section}_${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("PDF descargado");
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Error al generar PDF";
      if (e.response?.status === 403) toast.error("No tienes permiso para este reporte");
      else toast.error(detail);
    } finally { setBusyFn(false); }
  };

  if (variant === "compact") {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={() => generate(false)}
        disabled={busy}
        className={`gap-1 ${className}`}
        data-testid={`quick-report-${section}`}
        title="Descargar PDF"
      >
        <FilePdf size={14} weight="fill" /> PDF
      </Button>
    );
  }

  return (
    <div className={`flex gap-2 ${className}`}>
      <Button
        onClick={() => generate(false)}
        disabled={busy}
        size="sm"
        className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase gap-2"
        data-testid={`quick-report-${section}`}
      >
        <DownloadSimple size={14} weight="bold" />
        {busy ? "Generando…" : (label || "Reporte PDF")}
      </Button>
      {withShare && (
        <Button
          onClick={() => generate(true)}
          disabled={sharing}
          size="sm"
          variant="outline"
          data-testid={`quick-report-share-${section}`}
        >
          <Share size={14} className="mr-1" />
          Compartir
        </Button>
      )}
    </div>
  );
}
